/**
 * Data-room sync helper — reconciles a fresh batch of files from a connected
 * provider (Google Drive, Dropbox, …) with the existing DataRoomDocument list.
 *
 * Rules:
 *  - Match by `providerFileId`. Same id → same document, preserve comments.
 *  - New file (id not seen) → classify via heuristic, append.
 *  - Existing file with newer `modifiedAt` than the latest version → bump
 *    `currentVersion`, append entry to `versions[]`. Comments stay attached but
 *    will display as stale in the UI (commentedOnVersion < currentVersion).
 *  - Existing file with `typeOverridden` → keep manual type, skip re-classify.
 *  - Files removed from the provider are *not* deleted here — caller decides.
 */

import type { DataRoomDocument, DocumentVersion } from '@/lib/state/dataroom-store';
import { classifyByName } from './classify';
import { matchChecklistDoc } from './checklist-match';

export interface ProviderFile {
  /** Stable id from the provider (Drive fileId, Dropbox path_lower, etc.). */
  id: string;
  name: string;
  size?: string;
  /** Folder path inside the connected root (optional). Used for classification. */
  folderPath?: string;
  /** ISO-8601 last-modified timestamp from the provider. */
  modifiedAt?: string;
  /** ISO-8601 creation timestamp. Defaults to modifiedAt if absent. */
  createdAt?: string;
}

export interface SyncResult {
  documents: DataRoomDocument[];
  added: number;
  versionBumped: number;
  unchanged: number;
}

function nowIso(): string {
  return new Date().toISOString();
}

function newDocFromProvider(file: ProviderFile): DataRoomDocument {
  const cls = classifyByName(file.name, file.folderPath);
  const checklistMatch = matchChecklistDoc(file.name, file.folderPath);
  const uploadDate = file.createdAt ?? file.modifiedAt ?? nowIso();
  return {
    id: `dr_${file.id}`,
    name: file.name,
    type: cls.type,
    classificationConfidence: cls.confidence,
    typeOverridden: false,
    uploadDate,
    size: file.size ?? '—',
    status: 'pending',
    sharedWith: [],
    comments: [],
    providerFileId: file.id,
    currentVersion: 1,
    versions: [{ v: 1, uploadedAt: uploadDate, size: file.size, modifiedAt: file.modifiedAt }],
    linkedChecklistDocId: checklistMatch?.docId,
  };
}

function isNewerVersion(doc: DataRoomDocument, file: ProviderFile): boolean {
  if (!file.modifiedAt) return false;
  const versions = doc.versions ?? [];
  const latest = versions[versions.length - 1];
  if (!latest?.modifiedAt) return false;
  return new Date(file.modifiedAt).getTime() > new Date(latest.modifiedAt).getTime();
}

export function syncFromProvider(
  existing: DataRoomDocument[],
  providerFiles: ProviderFile[],
): SyncResult {
  const byProviderId = new Map<string, DataRoomDocument>();
  for (const d of existing) {
    if (d.providerFileId) byProviderId.set(d.providerFileId, d);
  }

  let added = 0;
  let versionBumped = 0;
  let unchanged = 0;
  const result: DataRoomDocument[] = [...existing];

  for (const file of providerFiles) {
    const match = byProviderId.get(file.id);

    if (!match) {
      result.push(newDocFromProvider(file));
      added += 1;
      continue;
    }

    if (isNewerVersion(match, file)) {
      const nextV = (match.currentVersion ?? 1) + 1;
      const newVersion: DocumentVersion = {
        v: nextV,
        uploadedAt: nowIso(),
        size: file.size,
        modifiedAt: file.modifiedAt,
      };
      const idx = result.findIndex((d) => d.id === match.id);
      if (idx >= 0) {
        result[idx] = {
          ...match,
          name: file.name, // provider may have renamed
          size: file.size ?? match.size,
          currentVersion: nextV,
          versions: [
            ...(match.versions ?? [{ v: 1, uploadedAt: match.uploadDate, size: match.size }]),
            newVersion,
          ],
        };
      }
      versionBumped += 1;
      continue;
    }

    unchanged += 1;
  }

  return { documents: result, added, versionBumped, unchanged };
}
